import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Button, CircularProgress, Container, Pagination, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { IPaginatedResponse } from '../../api/httpClient';
import { IGetMaterialPreviewResponse } from '../../api/material/iApiResponse';
import * as MaterialHelper from '../../api/material/materialHelper';
import { EStatus } from '../../shared/enum';
import { IMaterial } from '../../shared/iProject';
import { route } from '../../route/Index';
import { useNavigate } from 'react-router-dom';

interface IMaterialPanelProps {}

const MaterialPanelContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
`;

const MaterialPanel = (props: IMaterialPanelProps) => {
    let pageSize = 4;

    let timer = React.useRef<NodeJS.Timeout | null>(null);

    const navigate = useNavigate();

    const { t } = useTranslation();

    const [materialPreview, setMaterialPreview] = React.useState<IPaginatedResponse<IGetMaterialPreviewResponse>>();
    const [isLoading, setIsLoading] = React.useState(false);
    const [pageNumber, setpageNumber] = React.useState(1);
    const [filterName, setFilterName] = React.useState<string | null>(null);

    const fetchMaterialPreview = async (page: number, filter?: string) => {
        setIsLoading(true);
        let res = await MaterialHelper.getAllMaterialPreviewAsync(page, pageSize, filter);
        setMaterialPreview(res.data);
        setIsLoading(false);
    };

    React.useEffect(() => {
        fetchMaterialPreview(pageNumber);
    }, [pageNumber]);

    const handleGoToMaterial = (id: number) => navigate(route.backofficeMaterial.replace(':param', id.toString()));

    const handleChangeStatus = async (toTest: boolean, geometryId: number) => {
        let material = await MaterialHelper.getMaterialByIdAsync(geometryId);
        let payload: IMaterial = {
            ...material.data!,
            status: toTest ? EStatus.Test : EStatus.Deleted,
        };
        let res = await MaterialHelper.updateMaterialAsync(payload);
        if (res.status == 200) {
            fetchMaterialPreview(pageNumber);
            Swal.fire({
                title: t('updatedGeometry'),
                icon: 'success',
                toast: true,
                position: 'top',
                showConfirmButton: false,
                timer: 2000,
            });
        }
    };

    React.useEffect(() => {
        if (filterName !== null) {
            if (timer.current) {
                clearTimeout(timer.current);
            }
            timer.current = setTimeout(() => {
                fetchMaterialPreview(1, filterName);
            }, 1000);
        }
    }, [filterName]);

    return (
        <MaterialPanelContainer maxWidth='md'>
            <Stack height='100%' direction='column' spacing={4}>
                <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='h1' gutterBottom>
                        {t('material')}
                    </Typography>
                    <Button variant='orange' endIcon={<AddCircleIcon />} onClick={() => navigate(route.backofficeMaterial.replace(':param', 'newMaterial'))}>
                        {t('addNewMaterial')}
                    </Button>
                </Stack>
                <TextField
                    id='input-search'
                    placeholder={t('searchName')}
                    InputProps={{
                        type: 'search',
                    }}
                    onChange={e => setFilterName(e.target.value)}
                />
                {isLoading && <CircularProgress />}
                {!isLoading && materialPreview?.value?.length! > 0 && (
                    <>
                        <Stack flexGrow={1}>
                            <TableContainer>
                                <Table sx={{ minWidth: 650 }} size='small' aria-label='simple table'>
                                    <TableHead variant='greenLightWithoutBorder'>
                                        <TableRow>
                                            <TableCell>{t('name')}</TableCell>
                                            <TableCell align='center'>{t('lastUpdate')}</TableCell>
                                            <TableCell align='right'>{t('enabled')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {materialPreview!.value.map(x => (
                                            <TableRow key={x.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell onClick={() => handleGoToMaterial(x.id)} component='th' scope='row'>
                                                    {x.name}
                                                </TableCell>
                                                <TableCell onClick={() => handleGoToMaterial(x.id)} align='center'>
                                                    {format(x.lastUpdateDate, 'dd MMMM yyyy')}
                                                </TableCell>
                                                <TableCell align='right'>
                                                    <Switch
                                                        onChange={e => handleChangeStatus(e.target.checked, x.id)}
                                                        aria-label={`switch ${x.name}`}
                                                        checked={x.status == EStatus.Public || x.status == EStatus.Test}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Stack>
                        {materialPreview!.pageCount > 1 && (
                            <Stack alignItems='end'>
                                <Pagination
                                    showFirstButton
                                    showLastButton
                                    variant='outlined'
                                    shape='rounded'
                                    page={materialPreview?.pageNumber}
                                    count={materialPreview?.pageCount}
                                    onChange={(e, value) => setpageNumber(value)}
                                />
                            </Stack>
                        )}
                    </>
                )}
                {!isLoading && materialPreview?.value?.length! == 0 && <div>Empty</div>}
            </Stack>
        </MaterialPanelContainer>
    );
};

export default MaterialPanel;
